import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'results-glossary',
    category: 'results',
    title: 'Glossario — termini energetici in linguaggio semplice',
    summary: 'Ogni termine che appare nei grafici dei risultati e nelle schede KPI, raccolto in un unico posto.',
    body: (
      <>
        <p>Gli altri articoli di questa sezione rimandano qui. Aggiungilo ai preferiti come riferimento rapido.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Termine</th><th>Significato</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>kWh</strong> (chilowattora)</td>
              <td>Unità di <em>energia</em>. Un kWh ≈ un termoventilatore acceso per un'ora. Le bollette sono in kWh.</td>
            </tr>
            <tr>
              <td><strong>kW</strong> (chilowatt)</td>
              <td>Unità di <em>potenza</em> — la velocità con cui l'energia viene consumata in un dato momento. Un bollitore da 2 kW acceso per mezz'ora consuma 1 kWh.</td>
            </tr>
            <tr>
              <td><strong>Picco di domanda</strong></td>
              <td>La potenza massima (kW) assorbita in un periodo. Le tariffe di rete sono spesso legate a questo valore.</td>
            </tr>
            <tr>
              <td><strong>Generazione</strong></td>
              <td>Elettricità prodotta dai pannelli solari, dalla turbina eolica, ecc.</td>
            </tr>
            <tr>
              <td><strong>Carico / consumo</strong></td>
              <td>Elettricità utilizzata da edifici e apparecchiature.</td>
            </tr>
            <tr>
              <td><strong>Autoconsumo (%)</strong></td>
              <td>Di tutta l'energia <em>prodotta</em>, quale quota è stata consumata internamente
                (invece di essere esportata). Un valore alto significa che si spreca meno della propria generazione.</td>
            </tr>
            <tr>
              <td><strong>Autosufficienza (%)</strong></td>
              <td>Di tutta l'energia <em>consumata</em>, quale quota proveniva dalla propria generazione.
                Un valore alto significa minor dipendenza dalla rete.</td>
            </tr>
            <tr>
              <td><strong>Energia condivisa</strong> <em>(concetto)</em></td>
              <td>Energia prodotta da un membro della comunità e consumata nella stessa ora da un altro
                membro. Molti schemi nazionali premiano questa pratica. La piattaforma non mostra
                attualmente questa voce come KPI separato — emerge indirettamente attraverso l'autoconsumo e
                il grafico Bilancio Energetico.</td>
            </tr>
            <tr>
              <td><strong>Importazione / esportazione</strong></td>
              <td>Importazione = elettricità prelevata dalla rete. Esportazione = elettricità immessa in rete.</td>
            </tr>
            <tr>
              <td><strong>Tariffa</strong></td>
              <td>Il prezzo dell'elettricità, in €/kWh, spesso variabile ora per ora.</td>
            </tr>
            <tr>
              <td><strong>Stato di carica (SOC)</strong></td>
              <td>Il livello di carica di una batteria, da 0% (scarica) a 100% (piena).</td>
            </tr>
            <tr>
              <td><strong>NPV</strong> (Valore Attuale Netto)</td>
              <td>Il valore totale di un investimento nell'arco della sua vita utile, espresso in moneta odierna. <strong>Positivo = redditizio</strong>; negativo = perdita.</td>
            </tr>
            <tr>
              <td><strong>IRR</strong> (Tasso Interno di Rendimento) <em>(concetto)</em></td>
              <td>Il rendimento annuale percentuale dell'investimento. I risultati CBA della piattaforma
                mostrano attualmente NPV, BCR, payback, risparmi annuali e CO₂ — non l'IRR direttamente — ma
                il termine compare nella descrizione dello strumento.</td>
            </tr>
            <tr>
              <td><strong>BCR</strong> (Rapporto Benefici-Costi)</td>
              <td>Benefici totali divisi per i costi totali. Superiore a 1 = l'investimento si ripaga.</td>
            </tr>
            <tr>
              <td><strong>Payback (anni)</strong></td>
              <td>Quanti anni di risparmi sono necessari per coprire il costo iniziale.</td>
            </tr>
            <tr>
              <td><strong>MAPE</strong> (Errore Percentuale Assoluto Medio)</td>
              <td>Errore di previsione. Più basso è, meglio è — ad es. un MAPE del 10% significa che le previsioni
                si discostano in media del 10%.</td>
            </tr>
            <tr>
              <td><strong>GHI</strong></td>
              <td>Irradianza Globale Orizzontale — luce solare totale che colpisce una superficie piana, in W/m². Il principale dato meteorologico per le previsioni solari.</td>
            </tr>
          </tbody>
        </Table>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-forecast',
    category: 'results',
    title: 'Come leggere il grafico delle previsioni',
    summary: 'Cosa significano la linea, la banda ombreggiata e il numero di accuratezza.',
    body: (
      <>
        <p>
          Un risultato di previsione ha tre parti: una <strong>linea storica</strong>, una
          {' '}<strong>linea di previsione</strong> e una <strong>banda ombreggiata</strong> attorno alla
          previsione che mostra il range di incertezza.
        </p>
        <ScreenshotPlaceholder
          img="forecast-results.png"
          description="A finished forecast — line for past data, a forecast line, and a shaded band around the prediction. A MAPE value and a Confidence level badge (LOW / MEDIUM / MEDIUM-HIGH / HIGH) are shown alongside."
          source="Platform — Forecasting page, after a successful run. Capture the result panel on the right: chart + the MAPE/Confidence indicators. (A community with uploaded load data is needed.)"
          caption="Un tipico risultato di previsione."
        />

        <ul>
          <li><strong>Linea storica</strong> — quello che è realmente accaduto. Usala per verificare che i dati di input sembrino corretti.</li>
          <li><strong>Linea di previsione</strong> — la migliore stima singola del modello per il futuro.</li>
          <li><strong>Banda ombreggiata</strong> — un intervallo entro cui è probabile che cada il valore reale. Una banda <em>stretta</em> indica che il modello è sicuro; una banda <em>ampia</em> indica incertezza (spesso nel caso di orizzonti lunghi o con pochi dati di input).</li>
        </ul>

        <h5 className="mt-3">Due indicatori di accuratezza</h5>
        <p>
          La piattaforma mostra due indicatori riassuntivi accanto a ogni previsione — leggerli insieme:
        </p>

        <p className="mb-1"><strong>1. Livello di confidenza</strong> — un badge con uno di quattro valori:</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Badge</th><th>Significato</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>HIGH</strong></td><td>Molti dati di input puliti; risultati adatti a decisioni operative.</td></tr>
            <tr><td><strong>MEDIUM-HIGH</strong></td><td>Buono. Utilizzabile per la pianificazione.</td></tr>
            <tr><td><strong>MEDIUM</strong></td><td>Input funzionale ma imperfetto — verificare prima di agire sui casi limite.</td></tr>
            <tr><td><strong>LOW</strong></td><td>Dati di input limitati o segnale debole. Considerare la previsione come indicativa; aggiungere più dati storici.</td></tr>
          </tbody>
        </Table>

        <p className="mt-3 mb-1"><strong>2. MAPE</strong> (Errore Percentuale Assoluto Medio) — lo scarto medio tra previsione e realtà, in %. Guida indicativa del settore:</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>MAPE</th><th>Significato</th></tr>
          </thead>
          <tbody>
            <tr><td>&lt; 10%</td><td>Molto buono.</td></tr>
            <tr><td>10 – 20%</td><td>Buono. Utilizzabile per pianificazione e ottimizzazione.</td></tr>
            <tr><td>20 – 35%</td><td>Accettabile nelle fasi iniziali. Aggiungere dati o dati meteorologici per migliorare.</td></tr>
            <tr><td>&gt; 35%</td><td>Considerare come illustrativo. Vedere <ArticleLink to="tb-forecast-flat">«La mia previsione sembra piatta o errata»</ArticleLink>.</td></tr>
          </tbody>
        </Table>

        <Tip>
          Le previsioni solari sono di solito più accurate di quelle del carico, perché la luce solare è più
          prevedibile. Aggiungere un file meteorologico riduce ulteriormente il MAPE solare — vedere
          {' '}<ArticleLink to="data-weather">File meteorologici</ArticleLink>.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-optimisation-kpis',
    category: 'results',
    title: 'Come leggere i KPI dell\'ottimizzazione',
    summary: 'Cosa significa ogni scheda nel Riepilogo KPI dopo un\'esecuzione di Ottimizzazione.',
    body: (
      <>
        <p>
          Dopo un'esecuzione di ottimizzazione, sopra i grafici appare un <strong>Riepilogo KPI</strong>. Le
          schede visualizzate dipendono dal fatto che la comunità disponga di una batteria e se era
          disponibile una tariffa, ma il set standard è riportato di seguito.
        </p>
        <ScreenshotPlaceholder
          img="kpi-cards.png"
          description="The KPI Summary panel that appears after running an optimisation — cards for Daily Cost, Self-Sufficiency, Self-Consumption, Grid Import, Grid Import Cost, Grid Export Revenue, Peak Import and CO2 Avoided."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Capture the KPI Summary row that appears above the charts."
          caption="Il pannello Riepilogo KPI dal risultato dell'ottimizzazione."
        />

        <h5 className="mt-3">Ogni KPI spiegato</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>KPI</th><th>Cosa indica</th><th>Come migliorarlo</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Costo giornaliero</strong></td>
              <td>Costo totale di gestione della comunità per il giorno ottimizzato (importazioni meno ricavi da esportazione).</td>
              <td>Segnali tariffari migliori, più FV, una batteria dimensionata per le ore di picco.</td>
            </tr>
            <tr>
              <td><strong>Autosufficienza (%)</strong></td>
              <td>«Dell'energia che ho consumato, quanta proveniva dalla mia generazione?»</td>
              <td>Aggiungere più FV; ridurre i consumi; aggiungere accumulo.</td>
            </tr>
            <tr>
              <td><strong>Autoconsumo (%)</strong></td>
              <td>«Dell'energia che ho prodotto, quanta ne ho usata io stesso invece di esportarla?»</td>
              <td>Aggiungere una batteria; spostare i carichi flessibili nelle ore di sole.</td>
            </tr>
            <tr>
              <td><strong>Importazione dalla rete (kWh)</strong></td>
              <td>Energia totale acquistata dalla rete in quel giorno.</td>
              <td>Più FV e accumulo; carichi flessibili nelle ore solari.</td>
            </tr>
            <tr>
              <td><strong>Costo importazione rete</strong></td>
              <td>Quanto costano le importazioni in valuta. Combina volume × tariffa.</td>
              <td>Evitare le importazioni nelle ore di picco dei prezzi; precaricare nelle ore più economiche.</td>
            </tr>
            <tr>
              <td><strong>Ricavo esportazione rete</strong></td>
              <td>Quanto si è ricevuto per le esportazioni.</td>
              <td>Dipende dallo schema tariffario di esportazione; non è un obiettivo di per sé se si preferisce l'autoconsumo.</td>
            </tr>
            <tr>
              <td><strong>Picco di importazione (kW)</strong></td>
              <td>Prelievo massimo dalla rete ora per ora durante la giornata. Le tariffe di rete sono spesso legate a questo valore.</td>
              <td>Distribuire i carichi nel tempo; usare una batteria per il peak shaving.</td>
            </tr>
            <tr>
              <td><strong>CO2 evitata (kg)</strong></td>
              <td>Emissioni evitate rispetto al prelievo della stessa energia dalla rete. Dipende dall'energia FV/eolica autoconsumata.</td>
              <td>Massimizzare l'autoconsumo; dimensionare il FV al carico diurno.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Autoconsumo vs Autosufficienza — la coppia più confusa</h5>
        <p>Sembrano simili ma rispondono a domande opposte:</p>
        <ul>
          <li><strong>Autoconsumo</strong> riguarda la <em>propria generazione</em>: quanta di quella prodotta è stata usata?</li>
          <li><strong>Autosufficienza</strong> riguarda il <em>proprio consumo</em>: quanta di quella usata è stata prodotta internamente?</li>
        </ul>

        <h5 className="mt-3">Intervalli di verifica di coerenza</h5>
        <ul>
          <li>Una comunità con solo modesto FV su tetto e nessuna batteria: autoconsumo 60–90%, autosufficienza 10–25%.</li>
          <li>Una comunità con un grande impianto solare e una batteria: l'autosufficienza può superare il 50%, l'autoconsumo 80–95%.</li>
          <li>Un'autosufficienza superiore al ~90% significa che si importa a malapena — verificare che il file del carico non sia sottostimato.</li>
        </ul>
        <Warn>
          Se un KPI appare irrealisticamente buono (ad es. autosufficienza al 99% con solo modesto FV), vedere
          {' '}<ArticleLink to="tb-kpi-sanity-check">«I miei KPI sembrano troppo buoni o troppo cattivi»</ArticleLink>.
        </Warn>
      </>
    ),
  },
]
